import {nothing} from "lit";
import {Directive, directive, ElementPart, PartInfo, PartType} from "lit/directive.js";
import {Subscription} from 'rxjs';
import {AbstractLitElement} from "../components/abstract-lit.element";
import {FieldProxy} from "./field.proxy";
import {AbstractInputComponent} from "../components/_inputs/abstract-input.component";
import {Comparator} from "../comparators/comparator.interface";
import {DefaultComparator} from "../comparators/default.comparator";

export class BindToDirective<T> extends Directive {

    private _part: ElementPart | null = null;

    private _subscription: Subscription | null = null;

    private _listenerHandler!: EventListenerObject;

    constructor(partInfo: PartInfo) {
        super(partInfo);

        if (partInfo.type !== PartType.ELEMENT) {
            throw new Error('Die bindTo-Direktive kann nur auf Elemente angewendet werden.');
        }
    }

    private get _element(): AbstractInputComponent & { value: T } {
        return (this._part!.element as AbstractInputComponent & { value: T });
    }

    public render(fieldProxy: FieldProxy<T>, key: string, comparator: Comparator<any> = new DefaultComparator()) {
        return nothing;
    }

    public update(part: ElementPart, [fieldProxy, key, comparator]: [FieldProxy<T>, string, Comparator<any>] /*DirectiveParameters<this>*/): unknown {
        this._part = part;

        this._listenerHandler = <EventListenerObject>{
            handleEvent: ((event: CustomEvent<{ value: T }>) => fieldProxy.value = event.detail.value)
        };

        const element = this._element;
        element.value = fieldProxy.value as T;

        if (element instanceof AbstractInputComponent && fieldProxy.isRequired) {
            this._element.setAttribute(`required`, ``);
        }

        // Event-Listener für UI-Änderungen
        // noinspection TypeScriptValidateTypes
        element.addEventListener("value-changed", this._listenerHandler);

        // Reaktives Update bei Wertänderungen im FieldProxy
        this._subscription = fieldProxy.changedValue$.subscribe(this.updateElement.bind(this, fieldProxy));

        return this.render(fieldProxy, key!, comparator);
    }

    public disconnected(): void {
        const element = this._part?.element as (AbstractLitElement & { value: T });

        if (element) {
            // noinspection TypeScriptValidateTypes
            element.removeEventListener(`value-changed`, this._listenerHandler);
        }

        this._subscription?.unsubscribe();
    }

    private updateElement(fieldProxy: FieldProxy<any>, value: T): void {
        const element = this._element;
        if (!element) return;

        element.value = value;
        element.errors = fieldProxy.errors;
    }
}

const bindTo = directive(BindToDirective);

export {bindTo};